import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { PowerInput } from '@mui/icons-material';
import { useActiveVehicle } from '../../context/ActiveVehicleProvider';
import { stateProvinceList } from '../../data/stateProvinceList';

interface ScannedPlateHeaderProps {
	variant?: 'inline' | 'banner';
	licenseNumber: string;
	licenseState: string;
}

const ScannedPlateHeader: React.FC<ScannedPlateHeaderProps> = ({
	variant = 'banner',
	licenseNumber,
	licenseState,
}) => {
	const { scanImage } = useActiveVehicle();

	const stateObj = stateProvinceList.find((s) => s.code === licenseState);
	const stateLabel = stateObj ? stateObj.label : licenseState;
	const plateText = (licenseNumber || '').toUpperCase();

	const chipHeight = variant === 'inline' ? 28 : 32;
	const chipFontSize = variant === 'inline' ? '0.875rem' : '1rem';

	const chip = (
		<Chip
			label={
				<Box
					component="span"
					sx={{
						display: 'inline-flex',
						alignItems: 'baseline',
						gap: 0.75,
					}}
				>
					<Box
						component="span"
						sx={{ fontWeight: 600, letterSpacing: '0.05em' }}
					>
						{plateText || '—'}
					</Box>
					{stateLabel && (
						<Box
							component="span"
							sx={{
								fontWeight: 400,
								opacity: 0.75,
								fontSize: '0.8em',
							}}
						>
							{stateLabel}
						</Box>
					)}
				</Box>
			}
			sx={{
				height: chipHeight,
				fontSize: chipFontSize,
				bgcolor: 'background.paper',
				color: 'text.primary',
				border: 1,
				borderColor: 'primary.main',
				boxShadow: 1,
				'.MuiChip-label': { px: 1.25 },
			}}
		/>
	);

	if (scanImage) {
		return (
			<Box
				sx={{
					position: 'relative',
					width: '100%',
					aspectRatio: '2.35 / 1',
					border: 1,
					borderColor: 'divider',
					borderRadius: 2,
					overflow: 'hidden',
					backgroundImage: `url(${scanImage})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundColor: 'common.black',
				}}
			>
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						background:
							'linear-gradient(to top right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0) 60%)',
						pointerEvents: 'none',
					}}
				/>
				<Box
					sx={{
						position: 'absolute',
						bottom: 8,
						left: 8,
					}}
				>
					{chip}
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				position: 'relative',
				width: '100%',
				aspectRatio: '2.35 / 1',
				border: 1,
				borderColor: 'divider',
				borderRadius: 2,
				overflow: 'hidden',
				bgcolor: 'background.paper',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<PowerInput
				sx={{
					position: 'absolute',
					fontSize: variant === 'inline' ? 80 : 140,
					opacity: 0.08,
					color: 'text.primary',
					pointerEvents: 'none',
				}}
			/>
			<Typography
				variant="caption"
				sx={{
					position: 'absolute',
					top: 6,
					left: 10,
					fontStyle: 'italic',
					opacity: 0.6,
				}}
			>
				License Plate
			</Typography>
			<Box sx={{ position: 'absolute', bottom: 8, left: 8 }}>{chip}</Box>
		</Box>
	);
};

export default ScannedPlateHeader;
